import Flex from '@Components/Bases/Flex';
import StatementAlert from '@Components/Bases/StatementAlert';
import FormCheckbox from '@Components/Form/FormCheckBox';
import ChangeTypeModal from '@Components/Modal/ChangeTypeModal';
import { useDisclosure } from '@hooks/useDisclosure';
import { Box, InputLabel, Typography } from '@mui/material';
import { useRef } from 'react';
import { useGreenhouseForm } from '../../EsgGreenhouseFormProvider';
import {
  ICarbonFormValues,
  IGreenhouseGasOtherFormValues,
  INetZeroFormValues,
} from '../../EsgGreenhouseFormProvider/types';
import GreenhouseGoalRemark, {
  GH_GOAL_REMARK_ID,
} from './GreenhouseGoalRemark';
import { GhGoalValueType, ghGoalOptions } from './utils';

const GreenhouseGoal = () => {
  const {
    setValue,
    unregister,
    formState: { errors },
    watch,
    trigger,
  } = useGreenhouseForm();

  const [isDialogOpen, { open: openDialog, close: closeDialog }] =
    useDisclosure();
  const typeChange = useRef<GhGoalValueType>('other');

  const goalsError = errors.greenhouseGoalTerm?.greenhouseGoal?.goals;

  const completedSection = watch('greenhouseGoalTerm.completedSection');
  const { isCompletedTh, isCompletedEn } = completedSection ?? {};

  const handleGoalChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    option: GhGoalValueType
  ) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      setInitialFormValues(option);
    } else {
      setValue(`greenhouseGoalTerm.greenhouseGoal.goals.${option}`, true);
      typeChange.current = option;
      openDialog();
    }
    trigger('greenhouseGoalTerm.greenhouseGoal.goals');
  };

  const setInitialFormValues = (value: GhGoalValueType) => {
    switch (value) {
      case 'netZero':
        setValue('greenhouseGoalTerm.netZero', {} as INetZeroFormValues);
        break;
      case 'carbon':
        setValue('greenhouseGoalTerm.carbon', {} as ICarbonFormValues);
        break;
      case 'other':
        setValue(
          'greenhouseGoalTerm.other',
          {} as IGreenhouseGasOtherFormValues
        );
        break;
      default:
        break;
    }
  };

  const handleSubmitChange = () => {
    setValue(
      `greenhouseGoalTerm.greenhouseGoal.goals.${typeChange.current}`,
      false
    );
    unregister(`greenhouseGoalTerm.${typeChange.current}`);
    trigger('greenhouseGoalTerm.greenhouseGoal.goals');
    closeDialog();
  };

  return (
    <Flex mt={2} flexDirection="column" bgcolor="white">
      <Flex gap={1}>
        <Typography fontWeight={800}>
          การตั้งเป้าหมายของก๊าซเรือนกระจก
        </Typography>
        <Flex gap={0.5} alignItems="center">
          <GreenhouseGoalRemark />
        </Flex>
      </Flex>
      <Box mt={2}>
        <StatementAlert statement="กรุณาระบุเป้าหมายได้หลายข้อ" />
      </Box>
      <Flex sx={{ mt: 2 }} gap={2}>
        <InputLabel
          sx={{ textAlign: 'right', width: 1, maxWidth: 350 }}
          required
        >
          การตั้งเป้าหมายที่บริษัทมี
        </InputLabel>
        <Box>
          {ghGoalOptions.map((option) => (
            <FormCheckbox
              key={option.value}
              name={`greenhouseGoalTerm.greenhouseGoal.goals.${option.value}`}
              label={option.label}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleGoalChange(event, option.value)
              }
              inputProps={{ disabled: isCompletedTh || isCompletedEn }}
              sx={{
                '& .MuiCheckbox-root': { paddingTop: 0 },
                '& .MuiFormControlLabel-root': { alignItems: 'start' },
              }}
            />
          ))}
          {goalsError && (
            <Typography mt={2} variant={'caption'} color={'error.main'}>
              {goalsError.message}
            </Typography>
          )}
          {isDialogOpen && (
            <ChangeTypeModal
              opened={isDialogOpen}
              close={closeDialog}
              confirm={handleSubmitChange}
            />
          )}
        </Box>
      </Flex>
      <Box id={GH_GOAL_REMARK_ID} />
    </Flex>
  );
};

export default GreenhouseGoal;
